/**
 * What a player has asked the camera not to do, handed in rather than read.
 *
 * The eye rides the stride because somebody decided a walk should feel like a walk, and for a
 * player who cannot take a moving picture that decision is the whole game. Turning it off has to
 * happen at the amplitude, not over the finished frame: a view stripped of its bob afterwards is a
 * view the viewmodel is still bobbing against, and a filter that flattened both would flatten the
 * recoil with them.
 *
 * A parameter rather than a module a view reads, on the same terms
 * [sim/AGENTS.md](../AGENTS.md) states for the physics world: a step is a pure function of what it
 * is handed, and a preference read out of a global mid-resolve would be a camera that depends on
 * something no test can state and no replay carries. It never reaches state and never goes on the
 * wire, because it is one machine's answer about one player's eyes: two peers looking at the same
 * tick may quite correctly draw different amounts of bob.
 *
 * `bobScale` rather than a boolean, so the same seam already fits the day somebody wants half of
 * it. Zero is off at the source, one is what the character profile says.
 *
 * The base field of view rides the same seam, one figure per camera view, because it is the same
 * kind of thing: one machine's answer about one player's eyes. A mode's projection hands the
 * first-person figure to `fieldOfView` as the base an aim-down-sights zoom narrows from, so the
 * zoom stays relative to whatever the player chose, and the third-person view scales its own
 * figure by the same ratio.
 */

/** What both views open on, and what every one of them was hand-tuned against. */
export const DEFAULT_FOV_DEGREES = 75;

/** Narrow enough to feel like binoculars. Below this walking is seasickness. */
export const MIN_FOV_DEGREES = 60;

/** Wide enough for a fisheye. Above this the gun in the corner is more distortion than gun. */
export const MAX_FOV_DEGREES = 110;

export interface ViewPreference {
  /** How much of the character's own bob amplitude to use. 0 is none, 1 is all of it. */
  readonly bobScale: number;
  /** Base vertical field of view through this player's own eyes, before any sights narrow it. */
  readonly firstPersonFovDegrees: number;
  /** Base vertical field of view over this player's shoulder. Its own figure, not a copy. */
  readonly thirdPersonFovDegrees: number;
}

/** Everything the way it was authored. What a caller that has no player to ask hands over. */
export const FULL_VIEW_MOTION: ViewPreference = {
  bobScale: 1,
  firstPersonFovDegrees: DEFAULT_FOV_DEGREES,
  thirdPersonFovDegrees: DEFAULT_FOV_DEGREES,
};
